import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { InteractiveMap, Marker, Popup, FlyToInterpolator } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Newbox, MAPBOX_TOKEN } from "./LiveMapcomponent";
import { ReactComponent as LiveTrackFour } from "../../assets/livetracking/livetrackfour.svg";
import { ReactComponent as VideoIcon } from "../../assets/images/Video.svg";
import { ReactComponent as Videoon } from "../../assets/images/Video-On.svg";
import { ReactComponent as Info } from "../../assets/images/Info.svg";
import { ReactComponent as Infoon } from "../../assets/images/Info-On.svg";
import { ReactComponent as SpeedIcon } from "../../assets/images/speed (2).svg";

// Live map for accounts linked to TraKing. Same look as the standard Live
// Tracking map (LiveMapcomponent), but every value comes from TraKing's
// monitoring data. Fields TraKing doesn't provide (GPS/GSM signal, battery,
// lock) are left out rather than faked.

const FALLBACK = { latitude: -1.2921, longitude: 36.8219 }; // Nairobi
const MIN_ZOOM = 1;
const MAX_ZOOM = 20;
const STREETS = "mapbox://styles/mapbox/streets-v11";
const SATELLITE = "mapbox://styles/mapbox/satellite-streets-v11";

const RUN_STATUS = {
  moving: { text: "Driving", color: "#34C759", cls: "moving" },
  idle: { text: "Idling", color: "#FF9500", cls: "idling" },
  parked: { text: "Parking", color: "#FF9500", cls: "stationary" },
  offline: { text: "Offline", color: "#FF3B30", cls: "stationary" },
};
const runStatus = (v) => RUN_STATUS[v.state] || RUN_STATUS.offline;

const formatDuration = (seconds) => {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return [d ? `${d}d` : "", h ? `${h}h` : "", m || (!d && !h) ? `${m}min` : ""]
    .filter(Boolean)
    .join(" ");
};

// Same vehicle marker as TraKing's Monitoring page: a top-down car tinted by
// status and rotated to the vehicle's heading (heading_degree, 0 = north).
const STATUS_COLOR = {
  moving: "#11A156",
  idle: "#E28308",
  parked: "#8c8c8c",
  offline: "#F44336",
};

const normalizeHeading = (h) =>
  h === null || h === undefined || Number.isNaN(Number(h))
    ? 0
    : ((Number(h) % 360) + 360) % 360;

const MarkerIcon = ({ vehicle }) => {
  const color = STATUS_COLOR[vehicle.state] || STATUS_COLOR.parked;
  return (
    <div
      style={{
        width: 40,
        height: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          transform: `rotate(${normalizeHeading(vehicle.heading)}deg)`,
          transition: "transform 0.4s ease",
          display: "flex",
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 200" width="20" height="40">
          <rect x="7" y="65" width="86" height="12" rx="4" fill={color} />
          <rect x="15" y="10" width="70" height="180" rx="18" fill={color} />
          <rect x="22" y="68" width="56" height="64" rx="8" fill={color} />
          <path d="M 22 68 Q 50 48 78 68 L 74 82 Q 50 86 26 82 Z" fill="#2b3236" />
          <path d="M 26 132 Q 50 128 74 132 L 78 148 Q 50 152 22 148 Z" fill="#2b3236" />
          <rect x="20" y="11" width="12" height="6" rx="2" fill="#e8a317" />
          <rect x="68" y="11" width="12" height="6" rx="2" fill="#e8a317" />
          <rect x="18" y="180" width="12" height="7" rx="2" fill="#b31212" />
          <rect x="70" y="180" width="12" height="7" rx="2" fill="#b31212" />
        </svg>
      </div>
    </div>
  );
};

const NexusLiveMap = ({
  vehicles = [],
  hoveredVehicle = null,
  selectedVehicleFromList = null,
  vehicleSelectionCounter = 0,
  highlightedId = null,
}) => {
  const [viewport, setViewport] = useState({
    ...FALLBACK,
    zoom: 8,
    transitionDuration: 500,
  });
  const [mapStyle, setMapStyle] = useState(STREETS);
  const [selectedId, setSelectedId] = useState(null);
  const [isInfoActive, setIsInfoActive] = useState(true);
  const [isTrackingActive, setIsTrackingActive] = useState(false);
  const [locationName, setLocationName] = useState("Loading...");
  const fitted = useRef(false);

  const positioned = useMemo(
    () => vehicles.filter((v) => v.lat !== null && v.lng !== null),
    [vehicles],
  );
  const selected = useMemo(
    () => positioned.find((v) => v.id === selectedId) || null,
    [positioned, selectedId],
  );

  const flyTo = useCallback((lat, lng, zoom = 16, duration = 1000) => {
    setViewport((prev) => ({
      ...prev,
      latitude: lat,
      longitude: lng,
      zoom,
      transitionDuration: duration,
      transitionInterpolator: new FlyToInterpolator({
        speed: 1.2,
        curve: 2,
        screenSpeed: 0.5,
      }),
    }));
  }, []);

  const fitFleet = useCallback(() => {
    if (!positioned.length) return;
    const lats = positioned.map((v) => v.lat);
    const lngs = positioned.map((v) => v.lng);
    flyTo(
      (Math.min(...lats) + Math.max(...lats)) / 2,
      (Math.min(...lngs) + Math.max(...lngs)) / 2,
      positioned.length === 1 ? 14 : 8,
      800,
    );
  }, [positioned, flyTo]);

  // Fit the map to the fleet once, when the first positions arrive
  useEffect(() => {
    if (fitted.current || !positioned.length) return;
    fitted.current = true;
    fitFleet();
  }, [positioned, fitFleet]);

  const selectVehicle = useCallback(
    (v) => {
      setSelectedId(v.id);
      setIsInfoActive(true);
      setIsTrackingActive(false);
      if (v.lat !== null && v.lng !== null) flyTo(v.lat, v.lng);
    },
    [flyTo],
  );

  // Checking a vehicle in the list opens its popup
  useEffect(() => {
    if (selectedVehicleFromList) selectVehicle(selectedVehicleFromList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleSelectionCounter]);

  // Clicking a row in the list flies to the vehicle
  useEffect(() => {
    if (!hoveredVehicle || hoveredVehicle.lat === null || hoveredVehicle.lng === null) return;
    const timer = setTimeout(
      () => flyTo(hoveredVehicle.lat, hoveredVehicle.lng, 14, 700),
      80,
    );
    return () => clearTimeout(timer);
  }, [hoveredVehicle, flyTo]);

  // Reverse-geocode the selected vehicle's position
  useEffect(() => {
    if (!selected) return;
    setLocationName("Loading...");
    fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${selected.lng},${selected.lat}.json?access_token=${MAPBOX_TOKEN}`,
    )
      .then((res) => res.json())
      .then((data) =>
        setLocationName(data?.features?.[0]?.place_name || "Unknown location"),
      )
      .catch(() => setLocationName("Location unavailable"));
  }, [selected?.id, selected?.lat, selected?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  const zoom = (direction) =>
    setViewport((prev) => ({
      ...prev,
      zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev.zoom + (direction === "in" ? 0.5 : -0.5))),
      transitionDuration: 300,
    }));

  const renderInfoPanel = (v) => {
    const status = runStatus(v);
    const runText =
      v.stateSeconds != null
        ? `${status.text} (${formatDuration(v.stateSeconds)})`
        : status.text;
    const row = (label, value) => (
      <div style={{ fontSize: 13, color: "#333", marginBottom: 6 }}>
        {label} <span style={{ color: "#6B7280" }}>{value ?? "--"}</span>
      </div>
    );
    return (
      <Newbox>
        <div className="body-content">
          {row("Location:", locationName)}
          {row("Mileage:", v.odometer != null ? `${v.odometer} Km` : "--")}
          {row(
            "GPS Time:",
            v.lastPing ? dayjs(v.lastPing).format("hh:mm A • DD-MM-YYYY") : "--",
          )}
          {row("Run Status:", runText)}
          {row("Lat, Long:", `${v.lat}, ${v.lng}`)}
          {row("Driver:", v.driverName)}
          {row("Phone:", v.driverPhone)}
        </div>
      </Newbox>
    );
  };

  const renderTrackingPanel = (v) => (
    <div style={{ height: 234, width: "100%", borderTop: "1px solid #eee" }}>
      <InteractiveMap
        latitude={v.lat}
        longitude={v.lng}
        zoom={15}
        width="100%"
        height={234}
        mapStyle={STREETS}
        mapboxApiAccessToken={MAPBOX_TOKEN}
        scrollZoom
        dragPan
        attributionControl={false}
      >
        <Marker latitude={v.lat} longitude={v.lng} anchor="bottom">
          <LiveTrackFour style={{ width: "50px", height: "50px", cursor: "pointer" }} />
        </Marker>
      </InteractiveMap>
    </div>
  );

  return (
    <Newbox>
      <div style={{ width: "100%", height: "100vh", position: "relative" }}>
        <div
          style={{
            position: "absolute",
            top: 20,
            right: 20,
            zIndex: 1000,
            display: "flex",
            flexDirection: "row",
            gap: 10,
          }}
        >
          <button
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              border: "none",
              background: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            }}
            title="Reset Map"
            onClick={fitFleet}
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <circle cx="11" cy="11" r="4" stroke="#222" strokeWidth="2" />
              <circle cx="11" cy="11" r="1.5" fill="#222" />
              <line x1="11" y1="2" x2="11" y2="5" stroke="#222" strokeWidth="2" strokeLinecap="round" />
              <line x1="11" y1="17" x2="11" y2="20" stroke="#222" strokeWidth="2" strokeLinecap="round" />
              <line x1="2" y1="11" x2="5" y2="11" stroke="#222" strokeWidth="2" strokeLinecap="round" />
              <line x1="17" y1="11" x2="20" y2="11" stroke="#222" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>

          <div
            style={{
              display: "flex",
              borderRadius: "8px",
              overflow: "hidden",
              border: "1px solid #ccc",
              background: "#fff",
            }}
          >
            <button
              onClick={() => setMapStyle((p) => (p === STREETS ? SATELLITE : STREETS))}
              style={{
                padding: "5px 20px",
                border: "none",
                background: mapStyle === STREETS ? "#f0f0f0" : "#fff",
                outline: "none",
                cursor: "pointer",
              }}
            >
              Map/Satellite
            </button>
          </div>
        </div>

        <div className="zoom_container ">
          <div className="zoom_Body">
            <button
              className="ZoomIn_Btn"
              onClick={() => zoom("in")}
              style={{
                width: "40px",
                height: "40px",
                border: "none",
                background: "#fff",
                cursor: "pointer",
                fontSize: "20px",
                fontWeight: "bold",
                borderBottom: "1px solid #ccc",
              }}
            >
              +
            </button>
            <button className="ZoomOut_Btn" onClick={() => zoom("out")}>
              −
            </button>
          </div>
        </div>

        <InteractiveMap
          {...viewport}
          width="100%"
          height="100%"
          mapStyle={mapStyle}
          mapboxApiAccessToken={MAPBOX_TOKEN}
          attributionControl={false}
          doubleClickZoom={false}
          onViewportChange={(next) =>
            setViewport({
              latitude: next.latitude,
              longitude: next.longitude,
              zoom: Math.min(Math.max(next.zoom, MIN_ZOOM), MAX_ZOOM),
            })
          }
        >
          {positioned.map((v) => {
            const status = runStatus(v);
            return (
              <React.Fragment key={v.id}>
                <Marker
                  latitude={v.lat}
                  longitude={v.lng}
                  anchor="bottom"
                  offsetTop={20}
                  onClick={() => selectVehicle(v)}
                >
                  <div
                    className={`marker-wrapper ${highlightedId === v.id ? "wave-active wave-okay" : ""}`}
                  >
                    <div className={`vehicle-label-bubble ${status.cls}`}>
                      {v.name}
                      <div className="bubble-arrow"></div>
                    </div>
                    <div className="vehicle-icon wave-object">
                      <MarkerIcon vehicle={v} />
                    </div>
                  </div>
                </Marker>

                {selectedId === v.id && (
                  <Popup
                    latitude={v.lat}
                    longitude={v.lng}
                    closeButton={false}
                    closeOnClick
                    anchor="bottom"
                    offset={[0, -20]}
                    onClose={() => setSelectedId(null)}
                  >
                    <div className="unit-details-popup" style={{ userSelect: "text" }}>
                      <div style={{ background: "#E6FAF0", display: "flex", flexDirection: "column" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "5px 10px",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontWeight: 600, fontSize: 14, color: "#111" }}>
                              {v.name ?? "--"}
                            </span>
                            <span
                              style={{
                                background: status.color,
                                color: "#fff",
                                fontSize: 12,
                                padding: "2px 8px",
                                borderRadius: 16,
                                fontWeight: 500,
                              }}
                            >
                              {status.text}
                            </span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", padding: "5px 10px" }}>
                            <span
                              onClick={() => {
                                setIsInfoActive((p) => !p);
                                setIsTrackingActive(false);
                              }}
                              style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                            >
                              {isInfoActive ? (
                                <Infoon style={{ width: 30, height: 30 }} />
                              ) : (
                                <Info style={{ width: 30, height: 30 }} />
                              )}
                            </span>
                            <span
                              onClick={() => {
                                setIsTrackingActive((p) => !p);
                                setIsInfoActive(false);
                              }}
                              style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                            >
                              {isTrackingActive ? (
                                <Videoon style={{ width: 30, height: 30 }} />
                              ) : (
                                <VideoIcon style={{ width: 30, height: 30 }} />
                              )}
                            </span>
                          </div>
                        </div>

                        {isInfoActive && (
                          <div style={{ display: "flex", justifyContent: "center", padding: "4px 15px" }}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <SpeedIcon style={{ width: 30, height: 30, color: "#555" }} />
                              <span style={{ fontSize: 13, fontWeight: 500, color: "#333" }}>
                                {Math.round(v.speed)} km/h
                              </span>
                            </div>
                          </div>
                        )}

                        {isTrackingActive && (
                          <div style={{ fontSize: 13, fontWeight: 500, color: "#333", padding: "4px 15px" }}>
                            <strong>Location: </strong> {locationName}
                          </div>
                        )}
                      </div>

                      {isTrackingActive ? renderTrackingPanel(v) : renderInfoPanel(v)}
                    </div>
                  </Popup>
                )}
              </React.Fragment>
            );
          })}
        </InteractiveMap>
      </div>
    </Newbox>
  );
};

export default NexusLiveMap;
